import { getAccountKeyFromShellLocalStorage } from './../context/index'
import { authenticatedFetch } from '../auth'
import type { UserInfoQueryResponse, UserFromExternalAdmin, AccountInfoFromGraphQL } from './models'
import { environment } from '../../environments'
import type { JifApi, Principal, User } from '../context/models'
import { HTTP401 } from '../errors'
import type { TypedResponse } from '../auth/authenticatedFetch'
import { getShellLogger } from '../../common/logger'
import { getPbxIdFromJiveResourceName } from '../../packages/account-switcher/account-switcher-helper'
import { getJiveApiBaseUrl } from '../../core/environment'

export const getJifUser = async (jiveId: string) =>
  await getHttpDataResponse<JifApi>(`${getJiveApiBaseUrl()}/jif/v4/user/jiveId/${jiveId}`)

export const getPrincipal = async (userEmail: string) =>
  await getHttpDataResponse<Principal>(`${getJiveApiBaseUrl()}/v5/identity/v1/principals/${userEmail}`)

export const getRolesFromAccounts = (accounts?: readonly AccountInfoFromGraphQL[]) => {
  const roles: string[] = []
  accounts?.forEach(account => {
    ;(account.licenses ?? []).forEach(license => {
      roles.push(...(license.roles ?? []))
    })
  })
  return roles
}

export const getUserFromExternalAdmin = async (userKey: string) =>
  await getHttpDataResponse<UserFromExternalAdmin>(
    `${environment().iamBaseUrl}/ext-admin/rest/users/${userKey}?consistency=eventual`,
    { credentials: undefined },
  )

export const graphQLQuery = `{
              user {
                email
                createTime
                firstName
                key
                lastName
                locale
                timeZone
                location
              }
              accounts {
                name
                key
                unifiedAdmin
                slowChannelEnabled
                licenses {
                  accountKey
                  description
                  roles
                  channel
                  enabled
                  key
                  type
                }
                settings {
                  phone {
                    engagePlgOptOut
                    inboxPlgOptOut
                    textAssistantTermsOfServiceAccepted
                    textAssistantTermsOfServiceAcceptedByAdmin
                  }
                  meeting {
                    gotoAppEnabled
                    gotoAppEntitled
                    gotoAppProvisioned
                  }
                }
              },
            }`

export const getUserInfo = async () => {
  try {
    const response = await authenticatedFetch(`${environment().authUrl}/me/graphql`, {
      method: 'POST',
      headers: [['Content-Type', 'application/json']],
      body: JSON.stringify({
        query: graphQLQuery,
      }),
      credentials: 'omit',
    })
    const { data, errors }: UserInfoQueryResponse = await response.json()
    if (errors && errors.length > 0) {
      getShellLogger().error(errors)
      return
    }

    return data
  } catch (e) {
    getShellLogger().error('could not fetch User Info', e)
  }
}

export const getAccountKey = (userKey: string, accounts: readonly User[], pbxId?: string): string | undefined => {
  const accountMatchingPBXId = accounts.find(account => getPbxIdFromJiveResourceName(account.jrn) === pbxId)
  if (accountMatchingPBXId) {
    return accountMatchingPBXId.accountKey
  }
  const shellContextLocalStorageAccountKey = getAccountKeyFromShellLocalStorage(userKey)
  if (shellContextLocalStorageAccountKey) {
    return shellContextLocalStorageAccountKey
  }
  //if no account key is available in local storage, return first in list of accounts
  const firstAccount = accounts[0] ?? {}
  return firstAccount.accountKey
}

export const getHttpDataResponse = async <T = unknown>(input: RequestInfo, init?: RequestInit) => {
  try {
    const response = await authenticatedFetch<T>(input, init)
    return await response.json()
  } catch {
    return await ({} as Promise<T>)
  }
}

export const getHttpDataResponseWithHttp401 = async <T = unknown>(input: RequestInfo, init?: RequestInit) => {
  let response: TypedResponse<T>
  try {
    response = await authenticatedFetch<T>(input, init)
  } catch {
    response = { json: async () => ({}) } as TypedResponse<T>
  }

  if (response.status === 401) {
    throw new HTTP401('No Access')
  }

  return await response.json()
}
